import { useCallback, useMemo } from 'react';
import { useTabWorkspaceLayout } from '../../../shared/layout/useContentLayout'
import { POS_CART_WIDTH_WIDE, getPosGridColumns, getPosCatalogPageSizeOptions, getPosCatalogViewportPageSize } from '../../../shared/layout/posLayout';
import {
  POS_DESK_CATALOG_GRID_PADDING_TOP,
  POS_DESK_CATALOG_GUTTER,
} from '../../application/posLayout.constants';
import { operationalTabListHostStyle } from '../../../shared/layout/operationalTabListHost';
import { APP_SIDEBAR_WIDTH } from '../../../shared/layout/breakpoints';
import { spacing } from '../../../shared/theme/spacing';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { styles } from '../../components/posShared.styles';

type Params = {
  windowWidth: number;
  isWidePosLayout: boolean;
  /** FlatList viewport height (desk). */
  listViewportHeight?: number;
  /** ListHeader (desk toolbar) height. */
  listHeaderHeight?: number;
};

export function usePosCatalogLayout({
  windowWidth,
  isWidePosLayout,
  listViewportHeight = 0,
  listHeaderHeight = 0,
}: Params) {
  const { layoutWebShell } = useTabWorkspaceLayout();

  const catalogPaneWidth = useMemo(() => {
    if (!isWidePosLayout) return windowWidth;
    const shellPadding = 32;
    const sidebar = layoutWebShell ? APP_SIDEBAR_WIDTH : 0;
    return Math.max(320, windowWidth - sidebar - POS_CART_WIDTH_WIDE - shellPadding);
  }, [isWidePosLayout, layoutWebShell, windowWidth]);

  const numColumns = useMemo(
    () => getPosGridColumns(catalogPaneWidth, isWidePosLayout),
    [catalogPaneWidth, isWidePosLayout]
  );

  const catalogPageSizeConfig = useMemo(
    () => getPosCatalogPageSizeOptions(numColumns),
    [numColumns]
  );

  /** Desk: fill measured pane with complete rows; until measured, fall back to 3-row default. */
  const viewportPageSize = useMemo(() => {
    if (!isWidePosLayout) return null;
    // Wait for both layouts so we don't overshoot (header=0) then shrink.
    if (listViewportHeight <= 0 || listHeaderHeight <= 0) {
      return catalogPageSizeConfig.defaultSize;
    }
    return getPosCatalogViewportPageSize(numColumns, listViewportHeight, listHeaderHeight);
  }, [
    catalogPageSizeConfig.defaultSize,
    isWidePosLayout,
    listHeaderHeight,
    listViewportHeight,
    numColumns,
  ]);

  const catalogPaneStyle = [operationalTabListHostStyle.host, isWidePosLayout && styles.posCatalogColumn];

  const posCatalogListContentStyle = useCallback(
    (productCount: number, catalogLoading: boolean, hasDeskListHeader = true) => {
      // La barra del carrito mobile no overlayea la lista: no reservar su altura acá.
      // PC desk: padding del área productos (proto `.desk-products`).
      const base = {
        paddingBottom: spacing.sm,
        ...(isWidePosLayout
          ? {
              paddingHorizontal: POS_DESK_CATALOG_GUTTER,
              paddingTop: hasDeskListHeader ? 0 : POS_DESK_CATALOG_GRID_PADDING_TOP,
              paddingBottom: POS_DESK_CATALOG_GRID_PADDING_TOP,
              backgroundColor: ticketColors.counter,
            }
          : null),
      };
      if (productCount === 0 && !catalogLoading) {
        return [base, { flexGrow: 1 as const }];
      }
      return [base];
    },
    [isWidePosLayout],
  );

  return {
    catalogPaneWidth,
    numColumns,
    catalogPageSizeConfig,
    viewportPageSize,
    catalogPaneStyle,
    posCatalogListContentStyle,
  };
}
